/**
 * Where static assets come from.
 *
 * On the web this is a no-op: `assetUrl()` returns the same relative path with the same
 * cache-busting query it always did, and nothing else in here ever runs.
 *
 * On native the app ships as a thin shell — app code only, no catalog. Every `.json`,
 * model, texture and thumbnail is streamed from ASSET_ORIGIN on first use and cached to
 * disk, so the second launch works offline.
 *
 * Downloads go through `Filesystem.downloadFile`, which fetches natively rather than from
 * the WebView. That matters twice over: the asset host needs no `Access-Control-Allow-Origin`
 * (the WebView's same-origin policy never sees the request), and the bytes never cross the
 * Capacitor bridge as base64.
 */

// config.js re-exports assetUrl from here, so it cannot be imported back. The platform
// flags live in a leaf module both files import instead of being re-derived here.
import { IS_NATIVE } from "../platform/flags.js";
import { loadFilesystem } from "../platform/plugins.js";

export const BUILD_ID =
  typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev";

/** Origin serving the streamed catalog. Required for a native build. */
export const ASSET_ORIGIN = (import.meta.env?.VITE_ASSET_ORIGIN ?? "").replace(/\/$/, "");

const CACHE_ROOT = "feydom-assets";
// Bumped when the cache layout changes; unrelated to BUILD_ID, which changes every commit
// and would otherwise force a full re-download on every release.
const INDEX_FILE = `${CACHE_ROOT}/index.json`;
/** Shipped inside the app; its remote twin is what an update check diffs against. */
const MANIFEST_PATH = "data/asset-manifest.json";

/** logical path -> local file URL of a DOWNLOADED update (shadows the bundled copy) */
const localByPath = new Map();
/** logical path -> { h: contentHash, b: bytes } for downloaded updates */
let index = {};
/** logical path -> contentHash for what shipped inside the app bundle */
let bundledHashes = new Map();
/** in-flight downloads, so concurrent callers share one request */
const pending = new Map();

let Filesystem = null;
let Directory = null;

async function fs() {
  if (!Filesystem) {
    const mod = await loadFilesystem();
    Filesystem = mod.Filesystem;
    // Library, not Data: Capacitor maps Data to iOS Documents, which is iCloud-backed —
    // App Review rejects apps that back up re-downloadable content. Cache is the other
    // extreme; iOS purges it, which would silently break offline use.
    Directory = mod.Directory.Library;
  }
  return { Filesystem, Directory };
}

/** Strip a leading slash and any cache-busting query so one asset has one key. */
export function normalizePath(path) {
  return String(path).replace(/^\.?\//, "").split("?")[0];
}

const isAbsolute = (url) => /^(data:|blob:|https?:|capacitor:|file:)/i.test(url);

/**
 * SYNC. The URL to hand a loader, an <img>, or fetch() right now.
 * Web: unchanged. Native: the cached local file if present, else the remote origin.
 */
export function assetUrl(path) {
  if (!IS_NATIVE) return `${path}?v=${BUILD_ID}`;
  if (isAbsolute(path)) return path;
  const key = normalizePath(path);
  // Downloaded update wins, then the copy that shipped in the bundle, then the origin.
  const updated = localByPath.get(key);
  if (updated) return updated;
  if (bundledHashes.has(key)) return key;
  return `${ASSET_ORIGIN}/${key}`;
}

/** SYNC. Whether the asset is already on disk — drives offline UI state. */
export function isCached(path) {
  const key = normalizePath(path);
  return localByPath.has(key) || bundledHashes.has(key);
}

export class AssetUnavailableError extends Error {
  constructor(path, cause) {
    super(`Asset unavailable offline: ${path}`);
    this.name = "AssetUnavailableError";
    this.path = path;
    this.cause = cause;
  }
}

/** Load the on-disk index so `isCached`/`assetUrl` answer correctly from the first frame. */
export async function initAssetCache() {
  if (!IS_NATIVE) return;

  // The bundled manifest tells us what shipped inside the app, so assetUrl can return a
  // plain relative path for anything that has not been superseded by a download. It needs
  // nothing from the Filesystem plugin, so the fetch and the plugin's cold import overlap.
  const [, { Filesystem: FS, Directory: DIR }] = await Promise.all([
    (async () => {
      try {
        const response = await fetch(MANIFEST_PATH);
        const manifest = await response.json();
        bundledHashes = new Map(
          Object.entries(manifest?.files ?? {}).map(([path, entry]) => [path, entry.h]),
        );
      } catch (error) {
        // Without it every asset looks un-bundled and would stream — correct, just slower.
        console.warn("[assets] bundled manifest unreadable, treating catalog as remote:", error);
        bundledHashes = new Map();
      }
    })(),
    fs(),
  ]);
  try {
    const { data } = await FS.readFile({
      path: INDEX_FILE,
      directory: DIR,
      encoding: "utf8",
    });
    const parsed = JSON.parse(typeof data === "string" ? data : "");
    index = parsed?.files ?? {};
  } catch {
    index = {}; // first run, or a corrupted index — re-download rather than guess
  }

  // getUri is pure path concatenation — it does not stat — so resolving the cache root once
  // and joining locally is equivalent to asking per file, minus one bridge round trip each.
  // The index can hold the whole catalog after an update, so that difference is thousands.
  try {
    const { uri: root } = await FS.getUri({ path: CACHE_ROOT, directory: DIR });
    for (const key of Object.keys(index)) {
      localByPath.set(key, globalThis.Capacitor.convertFileSrc(`${root}/${key}`));
    }
  } catch (error) {
    // No cache directory yet: nothing is downloaded, so the bundled copies stand.
    console.warn("[assets] cache root unavailable, using bundled catalog:", error);
    index = {};
  }
}

let saveTimer = null;
function scheduleIndexSave() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => void saveIndex(), 500);
}

async function saveIndex() {
  if (!IS_NATIVE) return;
  const { Filesystem: FS, Directory: DIR } = await fs();
  try {
    await FS.writeFile({
      path: INDEX_FILE,
      directory: DIR,
      encoding: "utf8",
      recursive: true,
      data: JSON.stringify({ version: 1, files: index }),
    });
  } catch (error) {
    console.warn("[assets] index save failed:", error);
  }
}

/**
 * Ensure one asset is on disk and return the URL to load it from.
 * No-op on web. Throws AssetUnavailableError when offline and uncached.
 */
export async function ensureAsset(path) {
  if (!IS_NATIVE) return assetUrl(path);
  if (isAbsolute(path)) return path;

  const key = normalizePath(path);
  const cached = localByPath.get(key);
  if (cached) return cached;
  // Shipped in the bundle and not superseded — already on the device, nothing to fetch.
  if (bundledHashes.has(key)) return key;
  if (pending.has(key)) return pending.get(key);

  const job = download(key).finally(() => pending.delete(key));
  pending.set(key, job);
  return job;
}

async function download(key, expectedHash = null, expectedBytes = 0) {
  const { Filesystem: FS, Directory: DIR } = await fs();
  const url = `${ASSET_ORIGIN}/${key}`;
  try {
    await FS.downloadFile({
      url,
      path: `${CACHE_ROOT}/${key}`,
      directory: DIR,
      recursive: true,
    });
  } catch (error) {
    throw new AssetUnavailableError(key, error);
  }

  const { size } = await FS.stat({ path: `${CACHE_ROOT}/${key}`, directory: DIR });

  // A host that answers a missing path with its index page (200 + HTML) would otherwise
  // leave us caching a 50KB web page as a texture, permanently. The manifest knows what
  // each file should weigh, so mismatches are rejected rather than indexed.
  if (expectedBytes && size !== expectedBytes) {
    await FS.deleteFile({ path: `${CACHE_ROOT}/${key}`, directory: DIR }).catch(() => {});
    throw new AssetUnavailableError(
      key,
      new Error(`size mismatch: got ${size} bytes, manifest says ${expectedBytes}`),
    );
  }

  const { uri } = await FS.getUri({ path: `${CACHE_ROOT}/${key}`, directory: DIR });
  const local = globalThis.Capacitor.convertFileSrc(uri);

  // A .gltf is useless without the buffers it names, and GLTFLoader resolves those
  // relative to the .gltf URL — so they must land before the parent is considered cached,
  // or an interrupted download leaves a model that throws deep inside GLTFParser.
  if (key.endsWith(".gltf")) {
    await ensureGltfSiblings(key, local);
  }

  localByPath.set(key, local);
  index[key] = { h: expectedHash ?? null, b: size };
  scheduleIndexSave();
  return local;
}

async function ensureGltfSiblings(key, localUrl) {
  try {
    const response = await fetch(localUrl);
    const gltf = await response.json();
    const dir = key.slice(0, key.lastIndexOf("/") + 1);
    const uris = [...(gltf.buffers ?? []), ...(gltf.images ?? [])]
      .map((entry) => entry?.uri)
      .filter((uri) => uri && !/^(data:|https?:)/i.test(uri));
    await Promise.all(uris.map((uri) => ensureAsset(normalizeRelative(dir + uri))));
  } catch (error) {
    console.warn("[assets] could not resolve glTF siblings for", key, error);
  }
}

/** Collapse `a/b/../c` so a sibling reference can't escape the cache root. */
function normalizeRelative(path) {
  const parts = [];
  for (const segment of path.split("/")) {
    if (segment === "..") parts.pop();
    else if (segment !== "." && segment !== "") parts.push(segment);
  }
  return parts.join("/");
}

/**
 * Run `task` over every item with a bounded number in flight, reporting progress as each
 * settles. Failures are collected rather than thrown so one bad file cannot strand the
 * rest of a batch — the caller decides what a failure means.
 */
async function runPool(items, task, { concurrency = 6, onProgress, signal } = {}) {
  const total = items.length;
  if (!total) return [];

  let done = 0;
  let cursor = 0;
  const failures = [];

  async function worker() {
    while (cursor < items.length) {
      if (signal?.aborted) return;
      const item = items[cursor++];
      try {
        await task(item);
      } catch (error) {
        failures.push({ item, error });
      }
      done += 1;
      onProgress?.(done, total);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, total) }, () => worker()),
  );
  return failures;
}

/** Ensure many assets with a small concurrency pool. Reports progress as they land. */
export async function ensureAssets(paths, { concurrency = 6, onProgress } = {}) {
  if (!IS_NATIVE) return;
  const queue = [...new Set(paths.map(normalizePath))].filter((p) => !localByPath.has(p));

  const failures = await runPool(queue, (key) => ensureAsset(key), {
    concurrency,
    onProgress,
  });
  if (failures.length) {
    throw new AssetUnavailableError(failures[0].item, failures[0].error);
  }
}

/** Fetch a JSON asset, streaming and caching it first on native. */
export async function fetchAsset(path) {
  const url = IS_NATIVE ? await ensureAsset(path) : assetUrl(path);
  return fetch(url);
}

/**
 * Map logical paths to cached local files for everything THREE loads.
 *
 * `LoadingManager.resolveURL` is synchronous, so this cannot download — it is the lookup
 * half of the two-phase scheme, and `ensureAsset` must have run first. It is what covers
 * the sibling `.bin` a GLTFLoader requests internally, which app code never constructs.
 */
export function installURLModifier(loadingManager) {
  if (!IS_NATIVE || !loadingManager) return;
  loadingManager.setURLModifier((url) => {
    // RoomBuilder hands the loader a data: URL for the custom floor crop.
    if (isAbsolute(url)) return url;
    const key = normalizePath(url);
    const updated = localByPath.get(key);
    if (updated) return updated;
    if (bundledHashes.has(key)) return url;
    if (import.meta.env?.DEV) console.warn("[assets] uncached at load time:", key);
    return `${ASSET_ORIGIN}/${key}`;
  });
}

export async function cacheStats() {
  const files = Object.keys(index).length;
  // `download()` stores entries as { h, b } — reading `.bytes` here always reported 0.
  const bytes = Object.values(index).reduce((sum, e) => sum + (e?.b ?? 0), 0);
  return { files, bytes };
}

export async function clearAssetCache() {
  if (!IS_NATIVE) return;
  const { Filesystem: FS, Directory: DIR } = await fs();
  try {
    await FS.rmdir({ path: CACHE_ROOT, directory: DIR, recursive: true });
  } catch (error) {
    console.warn("[assets] clear failed:", error);
  }
  localByPath.clear();
  index = {};
  await saveIndex();
}

/**
 * Small assets that nearly every scene needs and that are loaded from synchronous call
 * sites (leg/wood/label textures in FurnitureManager, the door, the blue-noise mask).
 * Rewriting six sync `textureLoader.load` call sites to be async would ripple through the
 * furniture pipeline for ~2.4MB, so they are streamed once up front instead and resolved
 * from cache thereafter.
 */
export const CORE_ASSETS = [
  "textures/furniture_legs/light-legs-texture.jpg",
  "textures/furniture_legs/light-legs-normal-map.jpg",
  "textures/furniture_legs/dark-legs-texture.jpg",
  "textures/furniture_legs/dark-legs-normal-map.jpg",
  "textures/wood/oak-texture-light.jpg",
  "textures/wood/oak-texture-dark.jpg",
  "textures/wood/oak-texture-map.png",
  "textures/zip_shelf/oak_1k.jpg",
  "textures/zip_shelf/oak_nor_1k.png",
  "textures/furniture_label/feydom-label.jpg",
  "textures/feydom-logo-red.jpg",
  "models/doors/wooden_door.glb",
];

/** Stream the always-needed set. Warns rather than throws — a missing leg texture should
 *  not stop the catalog from loading. */
export async function ensureCoreAssets(onProgress) {
  if (!IS_NATIVE) return;
  try {
    await ensureAssets(CORE_ASSETS, { onProgress });
  } catch (error) {
    console.warn("[assets] core asset preload incomplete:", error);
  }
}


/**
 * Fetch JSON from the asset origin.
 *
 * Goes through `apiFetch` rather than a plain `fetch`: the WebView's origin is
 * `capacitor://localhost` / `https://localhost`, so a cross-origin request needs an
 * `Access-Control-Allow-Origin` header the asset host does not send. CapacitorHttp performs
 * the request natively, where the same-origin policy does not apply — the same reason asset
 * bytes go through Filesystem.downloadFile. Getting this wrong fails *quietly*: the update
 * check just reports "skipped" forever and no catalog update ever lands.
 *
 * Imported lazily because platform/http.js imports config.js, which re-exports this module.
 */
async function fetchRemoteJSON(url, signal) {
  const { apiFetch } = await import("../platform/http.js");
  const response = await apiFetch(url, { signal });
  if (!response.ok) throw new Error(`manifest ${response.status}`);
  return response.json();
}

/**
 * Diff the origin's manifest against what shipped in the bundle plus what has already
 * been downloaded, and fetch only the difference.
 *
 * This is what lets a fabric, model or price list change without a store submission:
 * assets are data, not code, so shipping them over the air is within both stores' rules.
 * The app's own JavaScript is never streamed — it stays in the bundle, which is the line
 * Apple's Guideline 2.5.2 actually draws.
 *
 * @returns {Promise<{checked:number, updated:number, bytes:number}>}
 */
export async function checkForAssetUpdates({ onProgress, signal } = {}) {
  if (!IS_NATIVE || !ASSET_ORIGIN) return { checked: 0, updated: 0, bytes: 0 };

  let remote;
  try {
    remote = await fetchRemoteJSON(`${ASSET_ORIGIN}/${MANIFEST_PATH}`, signal);
  } catch (error) {
    // Offline or origin down: the bundled catalog still works, so this is not fatal.
    console.warn("[assets] update check skipped:", error);
    return { checked: 0, updated: 0, bytes: 0 };
  }

  const entries = Object.entries(remote?.files ?? {});
  const stale = [];
  let bytes = 0;

  for (const [path, entry] of entries) {
    const hash = entry?.h;
    if (!hash) continue;
    if (index[path]?.h === hash) continue;        // already downloaded this exact version
    if (bundledHashes.get(path) === hash) continue; // the bundled copy is current
    stale.push({ path, hash, bytes: entry.b ?? 0 });
    bytes += entry.b ?? 0;
  }

  if (!stale.length) return { checked: entries.length, updated: 0, bytes: 0 };

  console.info(
    `[assets] ${stale.length} of ${entries.length} assets out of date (${(bytes / 1e6).toFixed(1)} MB)`,
  );

  // Downloaded through the same pool the first-run preload uses: a catalog release can
  // touch hundreds of files, and one-at-a-time turns a background update into minutes.
  // Progress counts attempts so the bar still advances past a failure; `updated` counts
  // only files that actually landed, since that is what gets reported and logged.
  let updated = 0;
  const failures = await runPool(
    stale,
    async ({ path, hash, bytes: expected }) => {
      // Force a download even when a bundled copy exists — that is the point.
      await download(normalizePath(path), hash, expected);
      updated += 1;
    },
    { onProgress, signal },
  );
  for (const { item, error } of failures) {
    console.warn("[assets] update failed for", item.path, error);
  }

  return { checked: entries.length, updated, bytes };
}
